import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, HelpCircle, Pizza, Ruler, Lightbulb, Calculator, Eye } from 'lucide-react';
import { Question } from '../../../types';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { VisualAidFactory, shouldShowVisualAid } from '../../visuals/VisualAidFactory';
import { StatusBadge } from './StatusBadge';
import { AnswerComparison } from './AnswerComparison';
import { ExplanationCard } from './ExplanationCard';
import { LatexRenderer } from '../../ui/LatexRenderer';
interface FeedbackDisplayProps {
  question: Question;
  wasCorrect: boolean;
  userAnswer: string;
  userAnswerIndex?: number;
  motivationalText: string;
  xpGained: number;
  onNext: () => void;
  isLastQuestion: boolean;
  mode?: 'full' | 'compact';
}
/**
 * Unified feedback display component
 * Orchestrates the feedback experience with staggered animations and contextual visuals.
 */
export function FeedbackDisplay({
  question,
  wasCorrect,
  userAnswer,
  userAnswerIndex,
  motivationalText,
  xpGained,
  onNext,
  isLastQuestion,
  mode = 'full'
}: FeedbackDisplayProps) {
  // Keyboard listener for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNext]);
  const correctAnswer = question.options[question.correctAnswer];
  const distractorExplanation = !wasCorrect && userAnswerIndex !== undefined ? question.distractorExplanations?.[userAnswerIndex.toString()] || 'Helaas, dat was niet het juiste antwoord. Kijk goed naar de uitleg hieronder om te zien hoe het wel moet!' : undefined;
  // Determine if we should show the visual aid section
  const showVisualAid = shouldShowVisualAid(question.visualAid, wasCorrect);
  // Determine contextual header for visual aid
  const getVisualHeader = () => {
    if (!question.visualAid) return {
      icon: Eye,
      text: 'Kijk maar eens'
    };
    switch (question.visualAid.type) {
      case 'fraction':
        return {
          icon: Pizza,
          text: 'Zo ziet het eruit'
        };
      case 'stairs':
        return {
          icon: Ruler,
          text: 'Het metriek trapje'
        };
      case 'rule':
        return {
          icon: Lightbulb,
          text: 'Onthoud dit goed'
        };
      case 'column':
        return {
          icon: Calculator,
          text: 'Kolomsgewijs uitgerekend'
        };
      case 'money':
        return {
          icon: Calculator,
          text: 'Even natellen'
        };
      default:
        return {
          icon: HelpCircle,
          text: 'Kijk maar eens'
        };
    }
  };
  const visualHeader = getVisualHeader();
  const VisualIcon = visualHeader.icon;
  return <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full" data-id="element-1247">
      {/* 1. STATUS BADGE */}
      <StatusBadge isCorrect={wasCorrect} motivationalText={motivationalText} xpGained={xpGained} mode={mode} data-id="element-1248" />

      <div className="space-y-4 mb-6" data-id="element-1249">
        {/* 2. ORIGINAL QUESTION (New) */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} data-id="element-1250">
          <Card className="p-4 bg-white border border-slate-200 shadow-sm" data-id="element-1251">
            <div className="text-xs font-bold uppercase text-slate-400 mb-2 tracking-wider" data-id="element-1252">
              De vraag was:
            </div>
            <div className="text-lg font-medium text-slate-800" data-id="element-1253">
              <LatexRenderer content={question.question} data-id="element-1254" />
            </div>
          </Card>
        </motion.div>

        {/* 3. ANSWER COMPARISON (Only if wrong) */}
        {!wasCorrect && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} data-id="element-1255">
            <AnswerComparison userAnswer={userAnswer} correctAnswer={correctAnswer} isCorrect={wasCorrect} mode={mode} data-id="element-1256" />
          </motion.div>}

        {/* 4. EXPLANATIONS */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} data-id="element-1257">
          <ExplanationCard explanation={question.explanation || ''} distractorExplanation={distractorExplanation} isCorrect={wasCorrect} mode={mode} data-id="element-1258" />
        </motion.div>

        {/* 5. VISUAL AID (Conditional Wrapper) */}
        {showVisualAid && question.visualAid && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-1259">
            <Card className="p-6 border border-slate-200 shadow-sm bg-slate-50 overflow-hidden" data-id="element-1260">
              <div className="flex items-center gap-2 mb-6 text-slate-700 font-bold border-b border-slate-200 pb-3" data-id="element-1261">
                <div className="p-2 bg-white rounded-lg border border-slate-200 shadow-sm" data-id="element-1262">
                  <VisualIcon className="w-5 h-5 text-indigo-600" data-id="element-1263" />
                </div>
                <span className="text-lg" data-id="element-1264">{visualHeader.text}</span>
              </div>
              <VisualAidFactory visualAid={question.visualAid} isCorrect={wasCorrect} data-id="element-1265" />
            </Card>
          </motion.div>}
      </div>

      {/* 6. ACTION BUTTON */}
      {mode === 'full' && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.25
    }} className="mt-auto pt-4" data-id="element-1266">
          <Button fullWidth size="xl" onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} className="h-16 text-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 group" data-id="element-1267">
            {wasCorrect ? 'Volgende vraag' : 'Ik snap het nu! 💡'}
            <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-1268" />
          </Button>
        </motion.div>}
    </div>;
}